import { vrocManager, vrocFactory, VROCComponentFactory } from '../../_VROC/VROCComponentFactory.js';
import { initTimePickers } from '../../_Common/app.js';

export class ServiceManager {
	// Константы типов трансферов
	static TRANSFER_TYPES = {
		ARRIVAL: 'arrival',
		DEPARTURE: 'departure',
		ONE_WAY: 'one-way',
		DISPOSAL: 'disposal'
	};

	constructor() {
		this.currentTable = null; // Текущая таблица для определения попапа
		this.activeComponent = null; // Активный компонент (если есть)
		this.orderId = this._getOrderId(); // Получаем ID заказа один раз
		this._registerComponent();

		// Сохраняем ссылку на текущий экземпляр для глобальной функции
		ServiceManager.currentInstance = this;
	}

	/**
	 * Регистрирует базовую конфигурацию компонента в менеджере
	 */
	_registerComponent() {
		const baseConfig = {
			template: 'mod_crm/sideshift_accoms', // Базовый шаблон, будет переопределяться
			node: '.popup.service-manager',
			attach: false, // Создаем компонент с загрузкой данных (для попапов)
			params: {},
			// Автоматически генерируемые действия
			autoActions: {
				createBlock: {
					successMessage: 'Блок успешно создан',
					errorMessage: 'Ошибка при создании блока'
				},
				loadBlock: {
					successMessage: 'Блок успешно загружен',
					errorMessage: 'Ошибка при загрузке блока'
				},
				saveField: {
					successMessage: 'Поле сохранено',
					errorMessage: 'Ошибка при сохранении поля'
				}
			},
			// Валидация данных
			validation: {
				createBlock: {
					table: { required: true },
					itemId: { required: true, type: 'number' },
					invoiceId: { required: true, type: 'number' }
				},
				loadBlock: {
					table: { required: true },
					blockId: { required: true, type: 'number' }
				},
				saveField: {
					field: { required: true },
					table: { required: true },
					invoiceId: { required: true, type: 'number' },
					slt_bed_types: { required: false } // Основные поля формы
				}
			},
			// Обработка ошибок
			onError: (message, error) => {
				console.error('ServiceManager error:', message, error);
			},
			// События жизненного цикла
			events: VROCComponentFactory.createEvents({
				beforeInit: () => {
					console.log('ServiceManager: Initialization started');
				},
				afterInit: () => {
					console.log('ServiceManager: Initialization completed');
				},
				beforeAction: (data) => {
					console.log('ServiceManager: Action started', data.action);
				},
				afterAction: (data) => {
					console.log('ServiceManager: Action completed', data.action);

					// После успешного выполнения действия открываем попап
					if (data.action === 'createBlock' || data.action === 'loadBlock') {
						console.log('ServiceManager: Opening popup after successful action');
						if (window.popupManager) {
							// Определяем имя попапа по типу сервиса из данных действия
							const popupName = this._getPopupNameForAction(data);
							if (popupName) {
								console.log(`ServiceManager: Opening popup: ${popupName}`);

								// Проверяем, что попап существует в DOM
								const popupElement = document.querySelector(`[data-popup-name="${popupName}"]`);
								if (popupElement) {
									console.log(`ServiceManager: Popup element found: ${popupName}`);
									window.popupManager.open(popupName);
									// Инициализируем timepicker после открытия попапа
									initTimePickers(popupElement);
								} else {
									console.error(`ServiceManager: Popup element NOT found in DOM: ${popupName}`);
									// Выводим все доступные попапы для отладки
									const allPopups = document.querySelectorAll('[data-popup-name]');
									console.log('Available popups:', Array.from(allPopups).map(p => p.getAttribute('data-popup-name')));
								}
							} else {
								console.error('ServiceManager: Could not determine popup name');
							}
						} else {
							console.error('ServiceManager: PopupManager not available');
						}
					}
				},
				error: (data) => {
					console.error('ServiceManager: Error occurred', data.message);
				},
				shown: () => {
					console.log('ServiceManager: Popup shown');
				},
				hidden: () => {
					console.log('ServiceManager: Popup hidden');
					// Сбрасываем ссылку на активный компонент при закрытии попапа
					if (this.activeComponent) {
						console.log('ServiceManager: Clearing active component reference');
						this.activeComponent = null;
					}
				}
			}),
			// Настройки уведомлений
			notifications: {
				show: (message, type) => {
					console.log(`[${type.toUpperCase()}] ${message}`);
					// Только ошибки показываем пользователю
					if (type === 'error') {
						alert('Ошибка: ' + message);
					}
					// Успешные сообщения просто логируем
				}
			}
		};

		// Регистрируем базовую конфигурацию в менеджере
		vrocManager.registerComponent('serviceManager', baseConfig);
	}

	/**
	 * Создает новый VROC компонент для конкретного действия
	 * @param {string} action - Действие (createBlock, loadBlock)
	 * @param {string} table - Тип сервиса (accoms, custom_tours, transfer, etc.)
	 * @param {Object} additionalParams - Дополнительные параметры (itemId, blockId, etc.)
	 * @returns {Promise<VROCComponent>} - Созданный компонент
	 */
	async _createComponentForAction(action, table, additionalParams = {}) {
		// Уничтожаем предыдущий активный компонент если он есть
		if (this.activeComponent) {
			console.log('ServiceManager: Destroying previous component', this.activeComponent.id);
			// Правильно уничтожаем компонент через менеджер
			vrocManager.destroyComponent(this.activeComponent.id);
			this.activeComponent = null;
		}

		// Определяем шаблон на основе типа сервиса
		const template = `mod_crm/sideshift_${table}`;

		// Создаем компонент с динамическими параметрами
		const component = await vrocManager.createComponent('serviceManager', {
			template: template,
			params: {
				action: action,
				table: table,
				...additionalParams
			}
		});

		this.activeComponent = component;
		console.log(`ServiceManager: Created component for ${action} with template ${template}`, additionalParams);

		return component;
	}

	/**
	 * Обновляет шаблон активного компонента
	 * @param {string} table - Тип сервиса
	 */
	updateActiveComponentTemplate(table) {
		if (this.activeComponent) {
			const template = `mod_crm/sideshift_${table}`;
			vrocManager.updateComponentTemplate(this.activeComponent.id, template);
			console.log(`ServiceManager: Updated template to ${template}`);
		}
	}

	/**
	 * Обновляет действия активного компонента
	 * @param {Object} newActions - Новые действия
	 */
	updateActiveComponentActions(newActions) {
		if (this.activeComponent) {
			vrocManager.updateComponentActions(this.activeComponent.id, newActions);
			console.log('ServiceManager: Updated component actions');
		}
	}

	/**
	 * Обновляет параметры активного компонента
	 * @param {Object} newParams - Новые параметры
	 * @param {boolean} reloadData - Перезагрузить данные
	 */
	async updateActiveComponentParams(newParams, reloadData = true) {
		if (this.activeComponent) {
			await vrocManager.updateComponentParams(this.activeComponent.id, newParams, reloadData);
			console.log('ServiceManager: Updated component params');
		}
	}

	/**
	 * Определяет имя попапа на основе данных действия
	 * @param {Object} actionData - данные действия
	 * @returns {string|null} имя попапа
	 */
	_getPopupNameForAction(actionData) {
		// Используем сохраненную таблицу вместо извлечения из данных действия
		const table = this.currentTable;

		if (!table) {
			console.error('ServiceManager: No current table set', actionData);
			return null;
		}

		console.log(`ServiceManager: Determining popup for table: ${table}`);

		const popupMap = {
			'accoms': 'hotel-manager',
			'custom_tours': 'excursion-manager',
			'transfer': 'transfer-manager',
			'visas': 'visa-manager',
			'services': 'cip-manager'
		};

		const popupName = popupMap[table];
		if (!popupName) {
			console.error(`ServiceManager: No popup mapping found for table: ${table}`);
			return null;
		}

		return popupName;
	}

	/**
	 * Создает новый блок сервиса
	 * @param {string} table - таблица (accoms, custom_tours, transfer, visas, services)
	 * @param {string} itemId - ID элемента сервиса
	 * @param {Object} params - дополнительные параметры (даты, машины, тип трансфера и т.д.)
	 * @param {HTMLElement} sourceElement - элемент, из которого вызывается создание блока (для извлечения параметров)
	 */
	async createBlock(table, itemId, params = {}, sourceElement = null) {
		try {
			console.log(`[ServiceManager] createBlock called with table: ${table}, itemId: ${itemId}`);

		// Сохраняем текущую таблицу для использования в событиях
		this.currentTable = table;


		// Если передан sourceElement, извлекаем параметры из него
		let elementParams = {};
		if (sourceElement && table === 'accoms') {
			elementParams = this._extractRoomCardParams(sourceElement);
			console.log(`[ServiceManager] Extracted room card params:`, elementParams);
		}

		// Получаем специфические параметры для типа сервиса (объединенные с переданными и параметрами элемента)
		// Приоритет: переданные params > параметры элемента > специфические параметры
		const combinedParams = { ...elementParams, ...params, itemId: itemId };
		const specificParams = this._getServiceSpecificParams(table, combinedParams);

		// Подготавливаем полные данные для компонента заранее
		// ВАЖНО: не включаем invoiceId если он null/undefined, чтобы не отправлять "null" как строку
		const componentParams = {
			itemId: itemId,
			...(this.orderId ? { invoiceId: this.orderId } : {}),
			...specificParams
			// Не добавляем дефолтные даты здесь, т.к. они уже включены в specificParams
		};

		// Создаем новый компонент для этого действия
		const component = await this._createComponentForAction('createBlock', table, componentParams);

		// Поскольку базовые параметры уже переданы в componentParams,
		// для serviceData оставляем только специфические параметры
		// ВАЖНО: не включаем invoiceId если он null/undefined
		const serviceData = {
			table: table,
			itemId: itemId,
			...(this.orderId ? { invoiceId: this.orderId } : {}),
			// Специфические параметры сервиса
			...specificParams
		};

		console.log(`[ServiceManager] createBlock parameters union:`);
		console.log(`  - Table: ${table}`);
		console.log(`  - Additional params:`, params);
		console.log(`  - Specific params:`, specificParams);
		console.log(`  - Component params (with base):`, componentParams);
		console.log(`  - Service data (specific only):`, serviceData);


		// Используем VROC компонент для создания блока
		console.log(`[ServiceManager] Calling createBlock with:`, serviceData);
		await component.createBlock(serviceData);

		} catch (error) {
			console.error(`Ошибка создания блока ${table}:${itemId}:`, error);
		}
	}

	/**
	 * Загружает существующий блок для редактирования
	 * @param {string} table - таблица (accoms, custom_tours, transfer, visas, services)
	 * @param {string} blockId - ID блока
	 * @param {Object} params - дополнительные параметры
	 */
	async loadBlock(table, blockId, params = {}) {
		try {
			const callTimestamp = Date.now();
			console.log(`[${callTimestamp}] [ServiceManager] loadBlock called with table: ${table}, blockId: ${blockId}`);

			// Сохраняем текущую таблицу для использования в событиях
			this.currentTable = table;

			// Получаем специфические параметры для типа сервиса (объединенные с переданными)
			const specificParams = this._getServiceSpecificParams(table, params);

			console.log(`[${callTimestamp}] [ServiceManager] loadBlock parameters union:`);
			console.log(`  - Table: ${table}`);
			console.log(`  - Additional params:`, params);
			console.log(`  - Specific params:`, specificParams);

			// Создаем новый компонент для этого действия
			const component = await this._createComponentForAction('loadBlock', table, {
				blockId: blockId,
				...specificParams
			});

			// Используем VROC компонент для загрузки блока
			const loadData = {
				table: table,
				blockId: blockId,
				// Добавляем специфические параметры
				...specificParams,
				// Добавляем переданные параметры (могут переопределить специфические)
				...params
			};
			console.log(`[${callTimestamp}] [ServiceManager] Calling loadBlock with:`, loadData);
			await component.loadBlock(loadData);

			console.log(`[${callTimestamp}] [ServiceManager] loadBlock completed`);

		} catch (error) {
			console.error(`Ошибка загрузки блока ${table}:${blockId}:`, error);
		}
	}

	// ===== СПЕЦИФИЧЕСКИЕ ПАРАМЕТРЫ СЕРВИСОВ =====

	/**
	 * Получает специфические параметры для типа сервиса (объединенные с переданными)
	 * @param {string} table - Тип сервиса (accoms, custom_tours, transfer, visas, services)
	 * @param {Object} additionalParams - Дополнительные параметры для объединения
	 * @returns {Object} Специфические параметры, объединенные с дополнительными
	 */
	_getServiceSpecificParams(table, additionalParams = {}) {
		switch (table) {
			case 'accoms':
				return this._getAccomsParams(additionalParams);
			case 'custom_tours':
				return this._getToursParams(additionalParams);
			case 'transfer':
				return this._getTransferParams(additionalParams);
			case 'visas':
				return this._getVisasParams(additionalParams);
			case 'services':
				return this._getServicesParams(additionalParams);
			default:
				console.warn(`[ServiceManager] Unknown service type: ${table}`);
				return additionalParams; // Возвращаем переданные параметры, если тип неизвестен
		}
	}

	/**
	 * Параметры для отелей (объединенные с дополнительными)
	 * @param {Object} additionalParams - дополнительные параметры
	 * @returns {Object} Параметры отеля
	 */
	_getAccomsParams(additionalParams = {}) {
		// Получаем параметры из формы фильтра отелей
		const hotelsFormData = this.getHotelsFormData();

		// Парсим даты из date_value (формат: "DD.MM.YYYY - DD.MM.YYYY")
		let checkinDate = this.getDefaultCheckinDate();
		let checkoutDate = this.getDefaultCheckoutDate();

		if (hotelsFormData.date_value) {
			console.log(`[ServiceManager] Parsing date_value: "${hotelsFormData.date_value}"`);
			const dateParts = hotelsFormData.date_value.split(' - ');
			if (dateParts.length === 2) {
				checkinDate = dateParts[0].trim();
				checkoutDate = dateParts[1].trim();
				console.log(`[ServiceManager] Parsed dates - checkin: ${checkinDate}, checkout: ${checkoutDate}`);
			} else {
				console.warn(`[ServiceManager] date_value has unexpected format: "${hotelsFormData.date_value}"`);
			}
		} else {
			console.log(`[ServiceManager] No date_value found, using defaults - checkin: ${checkinDate}, checkout: ${checkoutDate}`);
		}

		// Все параметры для отелей (включая специфические)
		const defaultParams = {
			// Даты (из date_value или по умолчанию)
			dte_checkin: checkinDate,
			dte_checkout: checkoutDate,
			// Гости (из формы)
			adults: hotelsFormData.adults || '1',
			children: hotelsFormData.children || [],
			int_participants: hotelsFormData.participants || '1',
			// Тип кровати (по умолчанию - двуспальная)
			slt_bed_types: hotelsFormData.bed_type || 1,
			// Дополнительная кровать (по умолчанию - нет)
			chk_exb: hotelsFormData.extra_bed || '0',
			// Завтрак (по умолчанию - нет)
			chk_breakfast: hotelsFormData.breakfast || '0'
		};

		// Добавляем выбранных гостей (mlt_travelers) если они есть
		if (hotelsFormData.mlt_travelers && hotelsFormData.mlt_travelers.length > 0) {
			defaultParams.mlt_travelers = hotelsFormData.mlt_travelers;
			console.log(`[ServiceManager] Adding selected travelers:`, hotelsFormData.mlt_travelers);
		}

		// Объединяем: дополнительные параметры могут переопределить дефолтные
		return {
			...defaultParams,
			...additionalParams
		};
	}

	/**
	 * Параметры для экскурсий/туров (объединенные с дополнительными)
	 * @param {Object} additionalParams - дополнительные параметры
	 * @returns {Object} Параметры тура
	 */
	_getToursParams(additionalParams = {}) {
		// Получаем параметры из формы фильтра экскурсий
		const excursionsFormData = this.getExcursionsFormData();

		// Все параметры для туров
		const defaultParams = {
			// Дата экскурсии (из формы или завтрашняя дата)
			dte_date: excursionsFormData.date_value || this.getDefaultCheckinDate(),
			// Машина (из формы или по умолчанию)
			slt_cars: excursionsFormData.car_id || '26',
			// Количество участников (из формы или 1)
			int_participants: excursionsFormData.participants || '1',
			// Взрослые
			adults: excursionsFormData.adults || '1',
			// Дети (массив возрастов)
			children: excursionsFormData.children || []
		};

		// Добавляем выбранных гостей (mlt_travelers) если они есть
		if (excursionsFormData.mlt_travelers && excursionsFormData.mlt_travelers.length > 0) {
			defaultParams.mlt_travelers = excursionsFormData.mlt_travelers;
			console.log(`[ServiceManager] Adding selected travelers for tour:`, excursionsFormData.mlt_travelers);
		}

		// Объединяем: дополнительные параметры могут переопределить дефолтные
		return {
			...defaultParams,
			...additionalParams
		};
	}

	/**
	 * Параметры для трансферов (объединенные с дополнительными)
	 * @param {Object} additionalParams - дополнительные параметры
	 * @returns {Object} Параметры трансфера
	 */
	_getTransferParams(additionalParams = {}) {
		// Получаем параметры из формы фильтра трансферов
		const transferFormData = this.getTransferFormData();

		const tomorrow = new Date();
		tomorrow.setDate(tomorrow.getDate() + 1);

		// Определяем тип трансфера (из параметров, формы или автоматически)
		const transferType = additionalParams.sel_type || transferFormData.sel_type || transferFormData.transfer_type || this._getTransferType();

		// Базовые параметры для трансферов
		const defaultParams = {
			// Дата трансфера (из формы или завтра)
			dte_date: transferFormData.date_value || tomorrow.toLocaleDateString('ru-RU'),
			// Тип трансфера
			sel_type: transferType,
			// Гости (из формы)
			adults: transferFormData.adults || '1',
			children: transferFormData.children || [],
			int_participants: transferFormData.participants || '1'
		};

		// Список всех возможных дополнительных полей трансфера (включая составные поля времени)
		const transferFields = [
			'dte_date',
			'pre_pickup',
			'pre_dropoff',
			'str_flight_number',
			'mlt_travelers',
			'num_hours',
			'dte_flightdate',
			'transfer_tme_pickuptime'  // Время выезда для departure (в часах до вылета)
		];

		// Список полей времени (с суффиксами _h и _m)
		const timeFields = [
			'tme_flighttime',
			'tme_pickuptime',
			'tme_dropofftime',
			'tme_time'
		];

		// Собираем дополнительные поля из формы (если они есть)
		const additionalFields = this._collectFieldsFromForm(
			'.transfer__control, .transfers__control',
			transferFields
		);

		// Собираем поля времени (проверяем как составные _h/_m, так и обычные)
		const timeFieldsData = this._collectTimeFields(
			'.transfer__control, .transfers__control',
			timeFields
		);

		console.log('[ServiceManager] Transfer additional fields from form:', additionalFields);
		console.log('[ServiceManager] Transfer time fields from form:', timeFieldsData);


		// Добавляем выбранных гостей (mlt_travelers) если они есть
		if (transferFormData.mlt_travelers && transferFormData.mlt_travelers.length > 0) {
			defaultParams.mlt_travelers = transferFormData.mlt_travelers;
			console.log(`[ServiceManager] Adding selected travelers for transfer:`, transferFormData.mlt_travelers);
		}

		// Объединяем: базовые параметры + поля из формы + поля времени + дополнительные параметры (последние переопределяют предыдущие)
		return {
			...defaultParams,
			...additionalFields,
			...timeFieldsData,
			...additionalParams
		};
	}

	/**
	 * Определяет тип трансфера по URL или элементу на странице
	 * @returns {string} Тип трансфера (arrival, departure, one-way, disposal)
	 */
	_getTransferType() {
		const { ARRIVAL, DEPARTURE, ONE_WAY, DISPOSAL } = ServiceManager.TRANSFER_TYPES;

		// Вариант 1: По URL
		const urlPath = window.location.pathname;
		if (urlPath.includes('/arrival')) return ARRIVAL;
		if (urlPath.includes('/departure')) return DEPARTURE;
		if (urlPath.includes('/one-way')) return ONE_WAY;
		if (urlPath.includes('/disposal')) return DISPOSAL;

		// Вариант 2: По data-атрибуту на странице
		const pageElement = document.querySelector('[data-transfer-type]');
		if (pageElement) {
			const type = pageElement.getAttribute('data-transfer-type');
			if (Object.values(ServiceManager.TRANSFER_TYPES).includes(type)) {
				return type;
			}
		}

		// Вариант 3: По скрытому полю
		const hiddenInput = document.querySelector('input[name="transfer_type"]');
		if (hiddenInput && hiddenInput.value) {
			const type = hiddenInput.value;
			if (Object.values(ServiceManager.TRANSFER_TYPES).includes(type)) {
				return type;
			}
		}

		// Вариант 4: По классу body
		const bodyClasses = document.body.className;
		if (bodyClasses.includes('transfer-arrival')) return ARRIVAL;
		if (bodyClasses.includes('transfer-departure')) return DEPARTURE;
		if (bodyClasses.includes('transfer-one-way')) return ONE_WAY;
		if (bodyClasses.includes('transfer-disposal')) return DISPOSAL;

		// По умолчанию
		console.warn('[ServiceManager] Could not determine transfer type, using default');
		return ARRIVAL;
	}

	/**
	 * Параметры для виз (объединенные с дополнительными)
	 * @param {Object} additionalParams - дополнительные параметры
	 * @returns {Object} Параметры визы
	 */
	_getVisasParams(additionalParams = {}) {
		// Проверяем, есть ли форма визы на странице
		const hasVisaForm = document.querySelector('.visa-form');

		if (hasVisaForm) {
			// Получаем данные из формы визы
			const visaFormData = this.getVisaFormData();

			console.log('[ServiceManager] Visa form detected, form data:', visaFormData);

			// Параметры визы из формы
			const visaParams = {
				// Срочность (0 - стандарт, 1 - срочная)
				chk_urgent: visaFormData.chk_urgent || '0',
				// Количество виз
				num_count: visaFormData.num_count || '1'
			};

			// Объединяем с переданными параметрами
			return {
				...visaParams,
				...additionalParams
			};
		}

		// Параметры по умолчанию для виз (если формы нет)
		const defaultParams = {
			// Тип визы (по умолчанию - туристическая)
			slt_visa_type: 'tourist',
			// Срочность (по умолчанию - обычная)
			slt_urgency: 'normal',
			// Количество копий (по умолчанию - 1)
			int_copies: '1'
		};

		// Объединяем: дополнительные параметры могут переопределить дефолтные
		return {
			...defaultParams,
			...additionalParams
		};
	}

	/**
	 * Параметры для дополнительных услуг (объединенные с дополнительными)
	 * @param {Object} additionalParams - дополнительные параметры
	 * @returns {Object} Параметры услуги
	 */
	_getServicesParams(additionalParams = {}) {
		// Проверяем, является ли это CIP сервисом (itemId === '1')
		// Если передан itemId в additionalParams или можем определить по наличию формы
		const isCIPService = additionalParams.itemId === '1' ||
							 additionalParams.itemId === 1 ||
							 document.querySelector('.cip-form');

		if (isCIPService) {
			// Получаем данные из CIP формы
			const cipFormData = this.getCIPFormData();

			console.log('[ServiceManager] CIP service detected, form data:', cipFormData);

			// Все параметры для CIP сервиса
			// Передаём массивы в формате dte_date[1], dte_date[2] для корректной работы с PHP
			const cipParams = {
				// Тип CIP (arrival/departure/round)
				cip_type: cipFormData.cip_type || 'arrival',
				// Даты (передаём с квадратными скобками)
				'dte_date[1]': cipFormData['dte_date[1]'] || '',
				'dte_date[2]': cipFormData['dte_date[2]'] || '',
				// Время прилёта (часы)
				'tme_time_h[1]': cipFormData['tme_time_h[1]'] || '',
				'tme_time_h[2]': cipFormData['tme_time_h[2]'] || '',
				// Время прилёта (минуты)
				'tme_time_m[1]': cipFormData['tme_time_m[1]'] || '',
				'tme_time_m[2]': cipFormData['tme_time_m[2]'] || '',
				// Номера рейсов
				'str_flight_number[1]': cipFormData['str_flight_number[1]'] || '',
				'str_flight_number[2]': cipFormData['str_flight_number[2]'] || '',
				// Гости
				adults: cipFormData.adults || '1',
				children: cipFormData.children || [],
				int_participants: cipFormData.participants || '1'
			};

			// Добавляем выбранных гостей (mlt_travelers) если они есть
			if (cipFormData.mlt_travelers && cipFormData.mlt_travelers.length > 0) {
				cipParams.mlt_travelers = cipFormData.mlt_travelers;
				console.log(`[ServiceManager] Adding selected travelers for CIP:`, cipFormData.mlt_travelers);
			}

			// Объединяем с переданными параметрами
			return {
				...cipParams,
				...additionalParams
			};
		}

		// Все параметры для обычных услуг (не CIP)
		const defaultParams = {
			// Дата услуги
			dte_date: this.getDefaultCheckinDate(),
			// Количество (по умолчанию - 1)
			int_quantity: '1',
			// Специальные требования (пустые по умолчанию)
			txt_special_requests: '',
            slt_flights: '10'
		};

		// Объединяем: дополнительные параметры могут переопределить дефолтные
		return {
			...defaultParams,
			...additionalParams
		};
	}

	// ===== ИЗВЛЕЧЕНИЕ ПАРАМЕТРОВ ИЗ ЭЛЕМЕНТОВ =====

	/**
	 * Универсальный метод для сбора полей из формы по списку имен
	 * @param {string} formSelector - CSS селектор формы
	 * @param {Array<string>} fieldNames - массив имен полей для поиска
	 * @returns {Object} Объект с найденными полями и их значениями
	 */
	_collectFieldsFromForm(formSelector, fieldNames) {
		const form = document.querySelector(formSelector);
		if (!form) {
			return {};
		}

		const collectedFields = {};

		fieldNames.forEach(fieldName => {
			// Ищем поле по name или по id
			let field = form.querySelector(`[name="${fieldName}"], #${fieldName}`);

			if (field) {
				// Получаем значение в зависимости от типа элемента
				let value = '';

				if (field.type === 'checkbox') {
					value = field.checked ? '1' : '0';
				} else if (field.type === 'radio') {
					// Для radio ищем выбранный элемент
					const checkedRadio = form.querySelector(`[name="${fieldName}"]:checked`);
					value = checkedRadio ? checkedRadio.value : '';
				} else if (field.tagName === 'SELECT') {
					// Для select берем выбранное значение
					value = field.value;
				} else {
					// Для обычных input
					value = field.value;
				}

				// Добавляем поле только если есть значение
				if (value !== '' && value !== null && value !== undefined) {
					collectedFields[fieldName] = value;
					console.log(`[ServiceManager] Collected field "${fieldName}": "${value}"`);
				}
			}
		});

		return collectedFields;
	}

	/**
	 * Собирает поля времени из формы (поддерживает как составные _h/_m поля, так и обычные)
	 * @param {string} formSelector - CSS селектор формы
	 * @param {Array<string>} timeFieldNames - массив базовых имен полей времени (без суффиксов)
	 * @returns {Object} Объект с найденными полями времени и их значениями
	 */
	_collectTimeFields(formSelector, timeFieldNames) {
		const form = document.querySelector(formSelector);
		if (!form) {
			return {};
		}

		const collectedFields = {};

		timeFieldNames.forEach(fieldName => {
			// Проверяем, есть ли составные поля с суффиксами _h и _m
			const hourField = form.querySelector(`[name="${fieldName}_h"], #${fieldName}_h`);
			const minField = form.querySelector(`[name="${fieldName}_m"], #${fieldName}_m`);

			if (hourField && minField) {
				// Если есть оба поля (_h и _m), собираем их отдельно
				const hourValue = hourField.value;
				const minValue = minField.value;

				if (hourValue !== '' && hourValue !== null && hourValue !== undefined) {
					collectedFields[`${fieldName}_h`] = hourValue;
					console.log(`[ServiceManager] Collected time field "${fieldName}_h": "${hourValue}"`);
				}

				if (minValue !== '' && minValue !== null && minValue !== undefined) {
					collectedFields[`${fieldName}_m`] = minValue;
					console.log(`[ServiceManager] Collected time field "${fieldName}_m": "${minValue}"`);
				}
			} else {
				// Если составных полей нет, проверяем обычное поле
				const field = form.querySelector(`[name="${fieldName}"], #${fieldName}`);

				if (field) {
					const value = field.value;

					if (value !== '' && value !== null && value !== undefined) {
						collectedFields[fieldName] = value;
						console.log(`[ServiceManager] Collected time field "${fieldName}": "${value}"`);
					}
				}
			}
		});

		return collectedFields;
	}

	/**
	 * Извлекает параметры из room-card элемента
	 * @param {HTMLElement} sourceElement - элемент кнопки или контейнер room-card
	 * @returns {Object} Параметры комнаты
	 */
	_extractRoomCardParams(sourceElement) {
		// Находим родительский элемент .room-card
		const roomCard = sourceElement.closest('.room-card');
		if (!roomCard) {
			console.warn('[ServiceManager] Room card not found for source element');
			return {};
		}

		const params = {};

		// Извлекаем тип кровати
		const bedTypeSelect = roomCard.querySelector('select[name="slt_bed_types"]');
		if (bedTypeSelect && bedTypeSelect.value) {
			params.slt_bed_types = bedTypeSelect.value;
		}

		// Извлекаем доп. кровать
		const extraBedSelect = roomCard.querySelector('select[name="chk_exb"]');
		if (extraBedSelect && extraBedSelect.value !== '') {
			params.chk_exb = extraBedSelect.value;
		}

		// Извлекаем завтрак
		const breakfastSelect = roomCard.querySelector('select[name="chk_breakfast"]');
		if (breakfastSelect && breakfastSelect.value !== '') {
			params.chk_breakfast = breakfastSelect.value;
		}

		console.log(`[ServiceManager] Room card params extracted:`, params);
		return params;
	}

	// ===== ЕДИНЫЕ МЕТОДЫ ПОЛУЧЕНИЯ ПАРАМЕТРОВ ИЗ ФОРМ =====

	/**
	 * Универсальный метод получения данных из любой формы
	 * @param {string} formSelector - CSS селектор формы
	 * @returns {Object} Данные формы
	 */
	_getFormData(formSelector) {
		const form = document.querySelector(formSelector);
		if (!form) {
			return {};
		}

		const formData = new FormData(form);
		const data = {};

		// Обрабатываем FormData правильно, учитывая массивы
		for (let [key, value] of formData.entries()) {
			// Если ключ заканчивается на [], это массив
			if (key.endsWith('[]')) {
				const cleanKey = key.slice(0, -2); // убираем []
				if (!data[cleanKey]) {
					data[cleanKey] = [];
				}
				data[cleanKey].push(value);
			} else {
				// Если уже есть значение с таким ключом, делаем массив
				if (data[key]) {
					if (!Array.isArray(data[key])) {
						data[key] = [data[key]];
					}
					data[key].push(value);
				} else {
					data[key] = value;
				}
			}
		}

		return data;
	}

	/**
	 * Получает данные гостей из guests-picker
	 * @param {string} guestsPickerSelector - CSS селектор guests-picker (по умолчанию .guests-picker)
	 * @returns {Object} Данные гостей
	 */
	_getGuestsData(guestsPickerSelector = '.guests-picker') {
		const guestsPicker = document.querySelector(guestsPickerSelector);
		if (!guestsPicker) {
			return {
				adults: '0',
				children: [],
				participants: '0',
				mlt_travelers: []
			};
		}

		let totalParticipants = 0;
		let adults = '0';
		const children = [];
		const mlt_travelers = [];

		// Проверяем, есть ли чекбоксы с гостями (режим выбора существующих гостей)
		const travelerCheckboxes = guestsPicker.querySelectorAll('.guests-picker__traveler-checkbox:checked');
		if (travelerCheckboxes.length > 0) {
			// Режим выбора существующих гостей
			travelerCheckboxes.forEach(checkbox => {
				mlt_travelers.push(checkbox.value);
			});
			totalParticipants = mlt_travelers.length;

			console.log('[ServiceManager] Found selected travelers:', mlt_travelers);

			return {
				adults: '0', // Не используем счётчик, т.к. гости уже выбраны
				children: [],
				participants: totalParticipants.toString(),
				mlt_travelers
			};
		}

		// Режим счётчика (гостей нет в заказе)
		// Считаем взрослых
		const adultsInput = guestsPicker.querySelector('.guests-picker__adults-input');
		if (adultsInput) {
			adults = adultsInput.value || '0';
			totalParticipants += parseInt(adults) || 0;
		}

		// Собираем детей с их возрастами
		const kidsItems = guestsPicker.querySelectorAll('.guests-picker__kids-item');
		kidsItems.forEach(item => {
			const ageSelect = item.querySelector('select, input[type="hidden"]');
			if (ageSelect && ageSelect.value) {
				children.push(ageSelect.value);
			}
		});

		totalParticipants += children.length;

		return {
			adults,
			children,
			participants: totalParticipants.toString(),
			mlt_travelers: [] // Пустой массив - гости будут созданы автоматически
		};
	}

	/**
	 * Получает данные из формы фильтра экскурсий (объединяет форму и гостей)
	 * @returns {Object} Полные данные формы экскурсий
	 */
	getExcursionsFormData() {
		// Получаем данные из формы
		const formData = this._getFormData('.excursions__control');
		
		// Получаем данные гостей
		const guestsData = this._getGuestsData('.guests-picker');

		// Объединяем данные
		return {
			...formData,
			...guestsData
		};
	}

	/**
	 * Получает данные из формы фильтра отелей
	 * @returns {Object} Данные формы отелей
	 */
	getHotelsFormData() {
		const formData = this._getFormData('.hotels__control, .accommodations__control');
		const guestsData = this._getGuestsData('.guests-picker');

		console.log('[ServiceManager] getHotelsFormData - formData:', formData);
		console.log('[ServiceManager] getHotelsFormData - guestsData:', guestsData);

		return {
			...formData,
			...guestsData
		};
	}

	/**
	 * Получает данные из формы фильтра трансферов
	 * @returns {Object} Данные формы трансферов
	 */
	getTransferFormData() {
		const formData = this._getFormData('.transfer__control, .transfers__control');
		const guestsData = this._getGuestsData('.guests-picker');

		return {
			...formData,
			...guestsData
		};
	}

	/**
	 * Получает данные из формы CIP сервиса
	 * @returns {Object} Данные формы CIP
	 */
	getCIPFormData() {
		const formData = this._getFormData('.cip-form');
		const guestsData = this._getGuestsData('.cip-form .guests-picker');

		return {
			...formData,
			...guestsData
		};
	}

	/**
	 * Получает данные из формы виз
	 * @returns {Object} Данные формы виз
	 */
	getVisaFormData() {
		const formData = this._getFormData('.visa-form');

		return {
			...formData
		};
	}

	// ===== ВСПОМОГАТЕЛЬНЫЕ МЕТОДЫ =====

	/**
	 * Получает ID заказа со страницы (приватный метод, вызывается в конструкторе)
	 * @returns {string|null} ID заказа
	 */
	_getOrderId() {
		// Вариант 1: Из скрытого поля #orderId
		const orderIdElement = document.querySelector('#orderId');
		if (orderIdElement && orderIdElement.value) return orderIdElement.value;

		// Вариант 2: Из data-атрибута
		const dataElement = document.querySelector('[data-invoice-id]');
		if (dataElement) {
			const idFromData = dataElement.getAttribute('data-invoice-id');
			if (idFromData) return idFromData;
		}

		// Вариант 3: Из скрытого поля invoice_id
		const hiddenInput = document.querySelector('input[name="invoice_id"]');
		if (hiddenInput && hiddenInput.value) return hiddenInput.value;

		// Вариант 4: Из URL параметра id
		const urlParams = new URLSearchParams(window.location.search);
		const idFromUrl = urlParams.get('id');
		if (idFromUrl) return idFromUrl;

		// Вариант 5: Из глобальной переменной
		if (window.currentInvoiceId) return window.currentInvoiceId;

		// По умолчанию возвращаем null
		return null;
	}

	/**
	 * Получает дату заезда по умолчанию (завтра)
	 * @returns {string} дата в формате DD.MM.YYYY
	 */
	getDefaultCheckinDate() {
		const tomorrow = new Date();
		tomorrow.setDate(tomorrow.getDate() + 1);
		return tomorrow.toLocaleDateString('ru-RU');
	}

	/**
	 * Получает дату выезда по умолчанию (через 2 дня)
	 * @returns {string} дата в формате DD.MM.YYYY
	 */
	getDefaultCheckoutDate() {
		const dayAfterTomorrow = new Date();
		dayAfterTomorrow.setDate(dayAfterTomorrow.getDate() + 2);
		return dayAfterTomorrow.toLocaleDateString('ru-RU');
	}

	/**
	 * Получает список туристов по умолчанию
	 * @returns {string} строка с ID туристов
	 */
	getDefaultTravelers() {
		// Ищем туристов на странице заказа
		const travelers = document.querySelectorAll('.travelers-manager__item input[type="checkbox"]:checked');
		if (travelers.length > 0) {
			return Array.from(travelers).map(t => t.value).join(',');
		}
		return '';
	}

	// ===== МЕТОДЫ ДЛЯ УПРАВЛЕНИЯ VROC КОМПОНЕНТОМ =====

	/**
	 * Показывает активный компонент
	 */
	show() {
		if (this.activeComponent) {
			this.activeComponent.show();
		} else {
			console.warn('ServiceManager: No active component to show');
		}
	}

	/**
	 * Скрывает активный компонент
	 */
	hide() {
		if (this.activeComponent) {
			this.activeComponent.hide();
		} else {
			console.warn('ServiceManager: No active component to hide');
		}
	}

	/**
	 * Уничтожает активный компонент
	 */
	destroyActiveComponent() {
		if (this.activeComponent) {
			// Находим ID компонента в менеджере и уничтожаем его
			const components = vrocManager.getComponentsByName('serviceManager');
			const componentToDestroy = components.find(c => c === this.activeComponent);

			if (componentToDestroy) {
				// Получаем ID экземпляра из имени компонента
				const instanceId = componentToDestroy.id;
				vrocManager.destroyComponent(instanceId);
			}

			this.activeComponent = null;
			console.log('ServiceManager: Active component destroyed');
		}
	}

	/**
	 * Уничтожает все компоненты ServiceManager
	 */
	destroyAllComponents() {
		vrocManager.destroyComponentsByName('serviceManager');
		this.activeComponent = null;
		console.log('ServiceManager: All components destroyed');
	}

	/**
	 * Обновляет параметры активного компонента
	 * @param {Object} newParams - новые параметры
	 */
	async updateParams(newParams) {
		if (this.activeComponent) {
			await this.activeComponent.updateParams(newParams);
		} else {
			console.warn('ServiceManager: No active component to update');
		}
	}

	/**
	 * Получает активный компонент
	 * @returns {VROCComponent|null}
	 */
	getActiveComponent() {
		return this.activeComponent;
	}

	/**
	 * Сохраняет поле формы компонента сервиса (экземплярный метод)
	 * @param {HTMLElement} element - элемент формы, который изменился
	 */
	async saveField(element) {
		// Получаем имя поля из элемента
		let field = element.name || element.getAttribute('name');

		// Для чекбоксов с массивами убираем []
		if (field && field.endsWith('[]')) {
			field = field.slice(0, -2);
		}

		console.log(`[ServiceManager] saveField called for field: ${field}`);

		if (!this.activeComponent) {
			console.warn('[ServiceManager] No active component to save field');
			return;
		}

		if (!field) {
			console.warn('[ServiceManager] No field name found for element:', element);
			return;
		}

		try {
			// Находим форму компонента
			const form = element.closest('form');
			if (!form) {
				console.error('[ServiceManager] Form not found for element:', element);
				return;
			}

			// Создаем FormData из формы
			const formData = new FormData(form);

			// Добавляем необходимые параметры
			formData.append('action', 'saveField');
			formData.append('field', field);
			// ВАЖНО: не добавляем invoiceId если он null/undefined
			if (this.orderId) {
				formData.append('invoiceId', this.orderId);
			}
			formData.append('table', this.currentTable || 'accoms');

			console.log(`[ServiceManager] 📝 Saving field "${field}" with ${Array.from(formData.entries()).length} FormData fields`);

			// Вызываем performAction напрямую с FormData
			const result = await this.activeComponent.performAction('saveField', formData);
			if (window.order)
				order.update();

			console.log(`[ServiceManager] ✅ Field "${field}" saved successfully`);

		} catch (error) {
			console.error(`[ServiceManager] ❌ Error saving field "${field}":`, error);
		}
	}



	/**
	 * Обновляет компоненты гостей после изменений в сервисе
	 */
	async _refreshGuestComponents() {
		try {
			console.log('[ServiceManager] _refreshGuestComponents called');

			// Получаем все активные компоненты гостей
			const guestComponents = vrocManager.getComponentsByName('guestsManager');
			console.log(`[ServiceManager] Found ${guestComponents.length} active guest components`);

			if (guestComponents.length === 0) {
				console.log('[ServiceManager] No active guest components to refresh');
				return;
			}

			// Обновляем каждый компонент гостей
			const refreshPromises = guestComponents.map(async (component) => {
				try {
					console.log(`[ServiceManager] Refreshing guest component ${component.id}`);
					await component.updateParams(component.params);
					console.log(`[ServiceManager] Guest component ${component.id} refreshed successfully`);
				} catch (error) {
					console.error(`[ServiceManager] Error refreshing guest component ${component.id}:`, error);
				}
			});

			await Promise.all(refreshPromises);
			console.log('[ServiceManager] All guest components refreshed after service change');

		} catch (error) {
			console.error('[ServiceManager] Error refreshing guest components:', error);
		}
	}

	/**
	 * Обновляет активный компонент сервиса (перезагружает данные)
	 */
	async refreshActiveComponent() {
		if (this.activeComponent) {
			console.log('[ServiceManager] Refreshing active component');
			try {
				// Обновляем параметры компонента (что приведет к перезагрузке данных)
				await this.activeComponent.updateParams(this.activeComponent.params);
				console.log('[ServiceManager] Active component refreshed successfully');
			} catch (error) {
				console.error('[ServiceManager] Error refreshing active component:', error);
			}
		} else {
			console.log('[ServiceManager] No active component to refresh');
		}
	}

	/**
	 * Статический метод для обновления всех активных компонентов сервисов
	 * Используется для обновления компонентов при изменениях в гостях
	 */
	static async refreshAllActiveComponents() {
		console.log('[ServiceManager] 🔄 Starting refresh of all active service components');

		// Получаем все компоненты сервисов
		const serviceComponents = vrocManager.getComponentsByName('serviceManager');
		
		// Фильтруем только компоненты с активными попапами
		const activeComponents = serviceComponents.filter(component => {
			if (!component.isPopup || !component.popupName) return false;
			
			const popup = document.querySelector(`.popup[data-popup-name="${component.popupName}"]`);
			const isActive = popup && popup.classList.contains('active');
			
			if (!isActive) {
				console.log(`[ServiceManager] 🚫 Skipping component ${component.id} - popup not active`);
			}
			
			return isActive;
		});
		
		console.log(`[ServiceManager] Found ${activeComponents.length} active components out of ${serviceComponents.length} total`);

		if (activeComponents.length === 0) {
			console.log('[ServiceManager] ❌ No active service components to refresh');
			return;
		}

		// Обновляем только активные компоненты
		const refreshPromises = activeComponents.map(async (component) => {
			try {
				console.log(`[ServiceManager] 📡 Refreshing active component ${component.id}`);
				await component.updateParams(component.params);
				console.log(`[ServiceManager] ✅ Component ${component.id} refreshed successfully`);
			} catch (error) {
				console.error(`[ServiceManager] ❌ Error refreshing component ${component.id}:`, error);
			}
		});

		await Promise.all(refreshPromises);
		console.log('[ServiceManager] 🎉 All active service components refreshed');
	}

	/**
	 * Проверяет, есть ли активные компоненты сервисов
	 * @returns {boolean}
	 */
	static hasActiveComponents() {
		const serviceComponents = vrocManager.getComponentsByName('serviceManager');
		
		// Фильтруем только компоненты с активными попапами
		const activeComponents = serviceComponents.filter(component => {
			if (!component.isPopup || !component.popupName) return false;
			
			const popup = document.querySelector(`.popup[data-popup-name="${component.popupName}"]`);
			return popup && popup.classList.contains('active');
		});
		
		const hasComponents = activeComponents.length > 0;
		console.log(`[ServiceManager] Checking for active components: ${hasComponents} (${activeComponents.length} active out of ${serviceComponents.length} total)`);
		return hasComponents;
	}

	/**
	 * Очищает неактивные компоненты ServiceManager (у которых попап закрыт)
	 */
	static cleanupInactiveComponents() {
		const serviceComponents = vrocManager.getComponentsByName('serviceManager');
		
		const inactiveComponents = serviceComponents.filter(component => {
			if (!component.isPopup || !component.popupName) return true; // Уничтожаем не-попап компоненты
			
			const popup = document.querySelector(`.popup[data-popup-name="${component.popupName}"]`);
			return !popup || !popup.classList.contains('active');
		});
		
		if (inactiveComponents.length > 0) {
			console.log(`[ServiceManager] 🧹 Cleaning up ${inactiveComponents.length} inactive components`);
			
			inactiveComponents.forEach(component => {
				console.log(`[ServiceManager] 💀 Destroying inactive component ${component.id}`);
				vrocManager.destroyComponent(component.id);
			});
			
			console.log('[ServiceManager] ✅ Cleanup completed');
		} else {
			console.log('[ServiceManager] ✅ No inactive components to clean up');
		}
	}

	/**
	 * Глобальная функция для обновления компонентов сервисов
	 * Можно использовать из любой части приложения
	 */
	static refreshServiceComponents = ServiceManager.refreshAllActiveComponents;

	/**
	 * Создаёт оба CIP сервиса (arrival и departure) и сразу добавляет их в заказ
	 * Без открытия попапа - для кнопки "Добавить оба в корзину"
	 */
	async createBothCIPAndAddToOrder() {
		try {
			console.log('[ServiceManager] createBothCIPAndAddToOrder called');

			// Получаем данные из CIP формы
			const cipFormData = this.getCIPFormData();
			console.log('[ServiceManager] CIP form data:', cipFormData);

			// Собираем параметры для отправки
			const params = {
				action: 'createAndAddToOrder',
				table: 'services',
				itemId: 1, // CIP service
				cip_type: 'round', // Оба направления
				invoiceId: this.orderId || '',
				// Даты
				'dte_date[1]': cipFormData['dte_date[1]'] || '',
				'dte_date[2]': cipFormData['dte_date[2]'] || '',
				// Время
				'tme_time_h[1]': cipFormData['tme_time_h[1]'] || '',
				'tme_time_h[2]': cipFormData['tme_time_h[2]'] || '',
				'tme_time_m[1]': cipFormData['tme_time_m[1]'] || '',
				'tme_time_m[2]': cipFormData['tme_time_m[2]'] || '',
				// Рейсы
				'str_flight_number[1]': cipFormData['str_flight_number[1]'] || '',
				'str_flight_number[2]': cipFormData['str_flight_number[2]'] || '',
				// Гости
				adults: cipFormData.adults || '1',
				children: cipFormData.children || []
			};

			// Если есть выбранные гости
			if (cipFormData.mlt_travelers && cipFormData.mlt_travelers.length > 0) {
				params.mlt_travelers = cipFormData.mlt_travelers;
			}

			console.log('[ServiceManager] Sending createAndAddToOrder request with params:', params);

			// Отправляем AJAX запрос
			const response = await fetch('/ajax.php?mid=47', {
				method: 'POST',
				headers: {
					'Content-Type': 'application/x-www-form-urlencoded',
				},
				body: this._buildFormData(params)
			});

			const result = await response.json();
			console.log('[ServiceManager] createAndAddToOrder response:', result);

			if (result.success) {
				console.log('[ServiceManager] Both CIP services added successfully');

				// Если был создан новый заказ, обновляем ID
				if (result.new_order) {
					console.log('[ServiceManager] New order created:', result.new_order);
					this.orderId = result.new_order;

					// Обновляем скрытое поле orderId
					const orderIdInput = document.getElementById('orderId');
					if (orderIdInput) {
						orderIdInput.value = result.new_order;
					}
				}

				// Меняем текст кнопки на "Добавлено"
				const addBothBtn = document.querySelector('.cip-results__add-both-btn');
				if (addBothBtn) {
					addBothBtn.textContent = 'Добавлено';
					addBothBtn.disabled = true;
				}

				// Обновляем список услуг в заказе
				if (window.crm) {
					await window.crm.refreshOrderServices();
				}
			} else {
				console.error('[ServiceManager] Error adding CIP services:', result.error);
				alert('Ошибка при добавлении сервисов: ' + (result.error || 'Неизвестная ошибка'));
			}

		} catch (error) {
			console.error('[ServiceManager] Error in createBothCIPAndAddToOrder:', error);
			alert('Ошибка при добавлении сервисов: ' + error.message);
		}
	}

	/**
	 * Преобразует объект параметров в строку для form-urlencoded запроса
	 * @param {Object} params - объект с параметрами
	 * @returns {string} закодированная строка
	 */
	_buildFormData(params) {
		const parts = [];

		for (const [key, value] of Object.entries(params)) {
			if (Array.isArray(value)) {
				// Для массивов добавляем каждый элемент отдельно
				value.forEach(v => {
					parts.push(encodeURIComponent(key + '[]') + '=' + encodeURIComponent(v));
				});
			} else if (value !== null && value !== undefined && value !== '') {
				parts.push(encodeURIComponent(key) + '=' + encodeURIComponent(value));
			}
		}

		return parts.join('&');
	}
}

// Глобальная функция для обновления компонентов сервисов (если нужно)
if (typeof window !== 'undefined') {
	window.refreshServiceComponents = ServiceManager.refreshAllActiveComponents;
}
